import { ReminderCard as DesignSystemReminderCard } from '@all-manager/design-system-v2';
import type { MouseEvent } from 'react';

import ServerErrorAlert from '../../../../../components/ServerErrorAlert';
import type { ReminderDatabase } from '../../../../../types';
import CreateUpdateEventModal from '../../../../events/external-components/CreateUpdateEventModal/CreateUpdateEventModal';
import DeleteReminderModal from '../../../../reminders/components/DeleteReminderModal/DeleteReminderModal';
import AddUpdateReminder from '../../../../reminders/external-components/AddUpdateReminder/AddUpdateReminder';
import TopicLink from '../../../../topics/components/TopicLink/TopicLink';
import ProviderLink from '../../../external-components/ProviderLink/ProviderLink';
import useReminderCard from './useReminderCard';

type Props = {
  reminder: ReminderDatabase;
  displayProvider?: boolean;
  displaySubject?: boolean;
  variant?: 'detailed' | 'condensed';
  href?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
};

export default function ReminderCard(props: Props) {
  const hook = useReminderCard(props);

  return (
    <>
      <ServerErrorAlert
        error={hook.completeReminderError ? (hook.completeReminderError.message ?? 'Une erreur est survenue.') : null}
      />

      <DesignSystemReminderCard
        variant={props.variant}
        href={props.href}
        onClick={props.onClick}
        reminder={props.reminder}
        displayProvider={props.displayProvider}
        displaySubject={props.displaySubject}
        renderProvider={props.reminder.provider_id ? () => (
          <ProviderLink
            providerId={props.reminder.provider_id!}
            variant={props.variant === 'condensed' ? 'caption' : 'body2'}
          />
        ) : undefined}
        renderSubject={props.reminder.subject_id ? () => (
          <TopicLink
            subTopicId={props.reminder.subject_id!}
            variant={props.variant === 'condensed' ? 'caption' : 'body2'}
          />
        ) : undefined}
        onComplete={hook.isAdmin && !props.reminder.is_completed ? () => {
          void hook.handleCompleteReminder('Rappel marqué comme terminé.');
        } : undefined}
        onCreateEvent={hook.isAdmin && !props.reminder.is_completed && props.reminder.subject_id ? hook.handleOpenCreateEventModal : undefined}
        onEdit={hook.canManage ? hook.handleOpenUpdateModal : undefined}
        onDelete={hook.canManage ? hook.handleOpenDeleteModal : undefined}
        isCompleting={hook.isPending}
      />

      {hook.isUpdateModalOpen ? (
        <AddUpdateReminder
          reminder={props.reminder}
          providerId={props.reminder.provider_id ?? ''}
          onClose={hook.handleCloseUpdateModal}
          displayProviderField={props.displayProvider}
          displaySubjectField={props.displaySubject}
        />
      ) : null}

      {hook.isDeleteModalOpen ? (
        <DeleteReminderModal
          reminder={props.reminder}
          onClose={hook.handleCloseDeleteModal}
        />
      ) : null}

      {hook.isCreateEventModalOpen && props.reminder.subject_id ? (
        <CreateUpdateEventModal
          subTopicId={props.reminder.subject_id}
          providerId={props.reminder.provider_id ?? undefined}
          onClose={hook.handleCloseCreateEventModal}
          initialValues={{
            title: props.reminder.title ?? '',
            description: props.reminder.description ?? '',
          }}
          onSuccess={() => {
            void hook.handleCreateEventSuccess();
          }}
        />
      ) : null}
    </>
  );
}
